// eslint-disable-next-line no-restricted-imports
import { TFunction } from 'i18next';
import { createBadgeColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/BadgeColumn';
import { createPrismaDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { DocumentsRes } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { createDocumentLayoutActionColumn } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/DashboardMain/utils';
import { Row } from '@tanstack/react-table';
import { RecordAny } from '@dealstack/shared/lib/common/common';

type DocumentListColumnsProps = {
	tCapManage: TFunction;
	tCommon: TFunction;
	classifierFilters: string[];
	onRowClick?: (row: Row<DocumentsRes>) => void;
	downloadFile?: (fileId: string, dealId: string, fileName: string) => void;
	previewFile?: (document: RecordAny, dealId: string) => void;
	removeDocument?: (row: Row<DocumentsRes>) => void;
	dealId?: string;
	targetId?: string;
};

export const getDocumentListColumns: ({
	tCapManage,
	tCommon,
	classifierFilters,
	onRowClick,
	downloadFile,
	previewFile,
	removeDocument,
	dealId,
	targetId,
}: DocumentListColumnsProps) => ExtendedColumnDef<DocumentsRes>[] = ({
	tCapManage,
	tCommon,
	classifierFilters,
	onRowClick,
	downloadFile,
	previewFile,
	removeDocument,
	dealId,
	targetId,
}) => [
	createTextColumn({
		headerText: tCapManage('transactions.document.table.columns.name'),
		filterText: tCapManage('transactions.document.table.filter.name'),
		accessorKey: 'name',
		showFilter: false, // set to false as global filter is used
		enableGlobalFilter: true,
		enableSorting: true,
	}),
	createBadgeColumn({
		headerText: tCapManage('transactions.document.table.columns.classifications'),
		filterText: tCapManage('transactions.document.table.filter.classifications'),
		showFilter: true,
		accessorKey: 'classifiers',
		enableColumnActions: false,
		enableSorting: false,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: {
			data: getClassifierFilters(classifierFilters),
		},
		badgeMapping: getBadgeTypes(tCommon, classifierFilters),
	}),
	createTextColumn({
		headerText: tCapManage('transactions.document.table.columns.related-to'),
		filterText: tCapManage('transactions.document.table.filter.related-to'),
		showFilter: false,
		accessorFn: (row) => {
			const names = Array.from(
				new Set(
					(row?.links ?? [])
						.flatMap((link) => [link.name, link.sellerName, link.buyerName])
						.filter(Boolean)
				)
			);

			const displayNames = names.slice(0, 5).join(', ') + (names.length > 5 ? '...' : '');

			return displayNames;
		},
		enableGlobalFilter: true,
		enableSorting: true,
	}),
	createPrismaDateColumn({
		headerText: tCapManage('transactions.document.table.columns.last-updated'),
		filterText: tCapManage('transactions.document.table.filter.last-updated'),
		accessorKey: 'updatedAt',
		showFilter: true,
		enableGlobalFilter: false,
		enableSorting: true,
		filterVariant: 'date-range',
		filterValueType: 'date-range',
		accessorFn: (row) => {
			if (!row.updatedAt) {
				return null;
			}
			return new Date(row.updatedAt);
		},
	}),
	...(onRowClick && downloadFile && previewFile && dealId && targetId
		? [
				createDocumentLayoutActionColumn({
					onRowClick,
					downloadFile,
					previewFile,
					removeDocument,
					dealId,
				}),
			]
		: []),
];

export enum DefaultDocumentClassifiers {
	agreements = 'Agreements',
	notices = 'Notices',
	corporateDocuments = 'Corporate documents',
	presentations = 'Presentations',
}

const getClassifierFilters = (classifierFilters: string[]) =>
	classifierFilters.map((classifier) => ({
		label: classifier,
		value: classifier,
	}));

export const getBadgeTypes = (tCommon: TFunction, classifiers: string[]) => {
	const defaultBadge = {
		backgroundColor: 'cyan.1',
		text: 'Unknown',
		fontColor: 'dark.8',
	};

	const badgeTypes = {
		[DefaultDocumentClassifiers.agreements]: {
			backgroundColor: 'blue.1',
			text: tCommon('classifier.badge.agreements'),
			fontColor: 'blue.9',
		},
		[DefaultDocumentClassifiers.presentations]: {
			backgroundColor: 'blue.2',
			text: tCommon('classifier.badge.presentations'),
			fontColor: 'blue.9',
		},
		[DefaultDocumentClassifiers.notices]: {
			backgroundColor: 'yellow.2',
			text: tCommon('classifier.badge.notices'),
			fontColor: 'blue.9',
		},
		[DefaultDocumentClassifiers.corporateDocuments]: {
			backgroundColor: 'red.1',
			text: tCommon('classifier.badge.corporateDocuments'),
			fontColor: 'blue.9',
		},
	};

	classifiers.forEach((classifier) => {
		if (!badgeTypes[classifier]) {
			badgeTypes[classifier] = {
				...defaultBadge,
				text: classifier,
			};
		}
	});

	return badgeTypes;
};
